'use client';

import * as React from 'react';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { Loader2 } from 'lucide-react';
import { FileTrigger } from 'react-aria-components';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { Camera, Edit } from '@avodah-inc/ui/icons';
import { cn } from '@avodah-inc/ui/utils';

import { Button } from '../button';

const avatarVariants = tv({
  base: 'flex justify-center items-center aspect-square select-none rounded-full shrink-0 align-middle relative',
  variants: {
    variant: {
      default: 'bg-primary text-base-white hover:bg-gray-500/90',
    },
    size: {
      xs: 'w-5 h-5 text-sm',
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-[1.25rem] leading-5',
      lg: 'w-14 h-14 text-[1.5rem] leading-10',
      xl: 'w-32 h-32 text-[4rem] leading-[4rem]',
    },
    isAddImage: {
      true: 'border border-primary bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export type AvatarSize = VariantProps<typeof avatarVariants>['size'];

export type AvatarProps = AvatarPrimitive.AvatarProps &
  VariantProps<typeof avatarVariants> & {
    initials?: string;
    src?: string | null;
    alt?: string;
    fallbackDelay?: number;
    showActionButton?: boolean;
    onAction?: (files: FileList | null) => void;
    iconSize?: number;
    backgroundColor?: string;
    isLoading?: boolean;
    dataTestId?: string;
  };

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, backgroundColor, ...props }, ref) => {
    React.useEffect(() => {
      if (props.dataTestId) {
        document
          .getElementsByClassName('react-aria-Input')[0]
          ?.setAttribute('data-testid', props.dataTestId);
      }
    }, []);

    return (
      <AvatarPrimitive.Root
        className={cn(
          avatarVariants({
            variant: props.variant,
            isAddImage: props.isAddImage,
            size: props.size,
          }),
          backgroundColor,
          className,
        )}
        ref={ref}
        {...props}
      >
        {!props.isLoading && (
          <AvatarPrimitive.Image
            alt={props.alt}
            className="aspect-square h-full w-full rounded-[inherit] object-cover"
            src={props.src ?? ''}
          />
        )}
        <AvatarPrimitive.Fallback
          className="flex h-full w-full items-center justify-center rounded-full"
          delayMs={props.fallbackDelay}
        >
          {props.isAddImage ? (
            <FileTrigger
              acceptedFileTypes={['image/png', 'image/jpeg']}
              onSelect={props.onAction}
            >
              <Button
                variant="text"
                isIconOnly
                className="focus:outline-white"
                size="xs"
                children={
                  <Camera
                    width={props.iconSize ?? 20}
                    height={props.iconSize ?? 20}
                    variant="primary"
                  />
                }
              />
            </FileTrigger>
          ) : (
            props.initials
          )}
        </AvatarPrimitive.Fallback>
        {props.showActionButton && props.src && (
          <FileTrigger
            acceptedFileTypes={['image/png', 'image/jpeg']}
            onSelect={props.onAction}
          >
            <Button
              variant="secondary"
              isIconOnly
              children={
                props.isLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin p-1" />
                ) : (
                  <Edit
                    width={props.iconSize ?? 40}
                    height={props.iconSize ?? 40}
                    variant="primary"
                  />
                )
              }
              className="absolute bottom-0 right-0 h-10 w-10 px-0"
            />
          </FileTrigger>
        )}
      </AvatarPrimitive.Root>
    );
  },
);

Avatar.displayName = 'Avatar';
